import { socialMedia, mediaType, mediaType_short } from '../config/dataCodes';

export const getSurveysWithExtractedFields = (_DATA, dataExists, type, brandPersonas) => {
  switch (type) {
    case 'brand':
      return dataExists
        ? _DATA.currentData().map(survey => ({
            Name: survey.Name,
            lastEdited: survey.lastEdited,
            dateCreated: survey.dateCreated,
            socialValueMatch: Math.round(survey.socialValueMatch) || undefined,
            productBenefitsMatch: Math.round(survey.productBenefitsMatch) || undefined,
            totalMatch: Math.round(survey.totalMatch) || undefined,
            filter: survey.filter,
            id: survey.id,
          }))
        : null;
    case 'buyer':
      return dataExists
        ? _DATA.currentData().map(survey => ({
            Name: survey.Name,
            lastEdited: survey.lastEdited,
            dateCreated: survey.dateCreated,
            socialValueMatch: Math.round(survey.socialValueMatch) || undefined,
            productBenefitsMatch: Math.round(survey.productBenefitsMatch) || undefined,
            totalMatch: Math.round(survey.totalMatch) || undefined,
            filter: survey.filter,
            id: survey.id,
          }))
        : null;
    case 'social': {
      return dataExists
        ? _DATA.currentData().map(survey => {
            return {
              postTitle: survey.Post_Title,
              lastEdited: survey.lastEdited,
              scheduledDate: survey.Post_Date,
              persona: getPersonaName(survey, brandPersonas),
              contentType: getPostMediaType_short(survey),
              platform: getSocialMediaPlatform(survey),
              impact: survey.impact,
              suggestion: survey.suggestion,
              filter: survey.filter,
              id: survey.id,
            };
          })
        : null;
    }
    case 'competitive-brand': {
      return dataExists
        ? _DATA.currentData().map(brandCalcResults => ({
            name: brandCalcResults.name,
            industry: brandCalcResults.industry,
            reputation: brandCalcResults.CReputation,
            relationship: brandCalcResults.CRelationship,
            brandChoice: brandCalcResults.CPurchaseChoice,
            loyalty: brandCalcResults.CLoyalty,
            IE_Focus: brandCalcResults.IE_Focus_Match,
            ideology: brandCalcResults.Ideology_Match,
            causes: brandCalcResults.Issues_Match,
            SV_Match: brandCalcResults.Values_Match,
            PB_Match: brandCalcResults.Product_Match,
            total_Match: brandCalcResults.Total_Match,
            id: brandCalcResults.id,
          }))
        : null;
    }
    default:
      return null;
  }
};

export const getCompetitiveSetBrandsSurveyWithExtractedFields = (_DATA, dataExists, width) => {
  if (width < 991) {
    return dataExists
      ? _DATA.currentData().map(brandCalcResults => ({
          name: brandCalcResults.name,
          industry: brandCalcResults.industry,
          total_Match: brandCalcResults.Total_Match,
          id: brandCalcResults.id,
        }))
      : null;
  }

  if (width > 991 && width < 1600) {
    return dataExists
      ? _DATA.currentData().map(brandCalcResults => ({
          name: brandCalcResults.name,
          industry: brandCalcResults.industry,
          SV_Match: brandCalcResults.Values_Match,
          PB_Match: brandCalcResults.Product_Match,
          total_Match: brandCalcResults.Total_Match,
          id: brandCalcResults.id,
        }))
      : null;
  }

  return dataExists
    ? _DATA.currentData().map(brandCalcResults => ({
        name: brandCalcResults.name,
        industry: brandCalcResults.industry,
        reputation: brandCalcResults.CReputation,
        relationship: brandCalcResults.CRelationship,
        brandChoice: brandCalcResults.CPurchaseChoice,
        loyalty: brandCalcResults.CLoyalty,
        IE_Focus: brandCalcResults.IE_Focus_Match,
        ideology: brandCalcResults.Ideology_Match,
        causes: brandCalcResults.Issues_Match,
        SV_Match: brandCalcResults.Values_Match,
        PB_Match: brandCalcResults.Product_Match,
        total_Match: brandCalcResults.Total_Match,
        id: brandCalcResults.id,
      }))
    : null;
};

export const getBrandConsumersWithExtractedFields = (_DATA, dataExists) => {
  return dataExists
    ? _DATA.currentData().map(brandConsumersCalcResults => ({
        name: brandConsumersCalcResults.brandName,
        quarter: brandConsumersCalcResults.quarter,
        year: brandConsumersCalcResults.year,
        id: brandConsumersCalcResults.id,
      }))
    : null;
};

export const getClientsWithExtractedFields = (_DATA, dataExists, cardSize) => {
  if (!dataExists) return null;

  if (cardSize === 'big') {
    return _DATA.currentData().map(clients => ({
      agencyId: clients.agencyId,
      name: clients.name,
      createdAt: clients.createdAt,
      userCount: clients.userCount,
      brandPersonaSurveyCount: clients.brandPersonaSurveyCount,
      buyerPersonaSurveyCount: clients.buyerPersonaSurveyCount,
      socialPostSurveyCount: clients.socialPostSurveyCount,
    }));
  } else if (cardSize === 'medium') {
    return _DATA.currentData().map(clients => ({
      agencyId: clients.agencyId,
      name: clients.name,
      brandPersonaSurveyCount: clients.brandPersonaSurveyCount,
      buyerPersonaSurveyCount: clients.buyerPersonaSurveyCount,
      socialPostSurveyCount: clients.socialPostSurveyCount,
    }));
  } else {
    return _DATA.currentData().map(clients => ({
      agencyId: clients.agencyId,
      name: clients.name,
      totalCount: clients.brandPersonaSurveyCount + clients.buyerPersonaSurveyCount + clients.socialPostSurveyCount,
    }));
  }
};

export const getTransformedYourBrandAndConsumersData = (yourBrandAndConsumersData, batchCountry) => {
  if (!yourBrandAndConsumersData) return [];
  const recodedData = Object.entries(yourBrandAndConsumersData).flatMap(([brandName, years]) =>
    Object.entries(years).flatMap(([year, quarters]) =>
      quarters.map(quarterWithIncrement => {
        const [quarter, increment] = quarterWithIncrement.split('_');
        return {
          brandName,
          quarter: quarterWithIncrement,
          year: Number(year),
          id: `${brandName}/batch_${quarter}_${year}_${batchCountry}_${increment}`,
        };
      }),
    ),
  );

  return recodedData.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.quarter.localeCompare(a.quarter, undefined, { numeric: true });
  });
};

const getPersonaName = (survey, brandPersonas) => {
  const selectedPersona = brandPersonas.find(brand => brand.id == survey['Post_Brand_Persona']);

  return selectedPersona?.Name ?? '';
};

export const getSocialMediaPlatform = ({ Post_Social_Media }) => {
  if (!Post_Social_Media) return undefined;

  const { platforms } = socialMedia;

  const selectedPlatform = platforms.find(platform => platform.value == Post_Social_Media);

  return selectedPlatform.text;
};

export const getPostMediaType = ({ Post_Media_Type }) => {
  const selectedMediaType = mediaType.find(mediaType => mediaType.value == Post_Media_Type);

  return selectedMediaType.text;
};

export const getPostMediaType_short = ({ Post_Media_Type }) => {
  const selectedMediaType = mediaType_short.find(mediaType_short => mediaType_short.value == Post_Media_Type);

  return selectedMediaType.text;
};
